import api from './api';

// Le immagini arrivano con ``url`` pubblico del bucket (ADR 0005): ``path`` è
// la chiave, l'URL lo calcola il backend alla risposta.

export type BannerSlot = 'hero' | 'sidebar';
export type BannerAudience = 'tutti' | 'anonimi' | 'autenticati';
export type BannerVariant = 'desktop' | 'mobile';

export interface BannerImage {
  path: string;
  url: string;
}

export interface Banner {
  id: string;
  slot: BannerSlot;
  audience: BannerAudience;
  /** Obbligatorio: è l'intero nome accessibile del banner, che non ha altro testo. */
  alt: string;
  /** Se null il banner si rende come <div>, non come <Link>. */
  href: string | null;
  desktopImage: BannerImage | null;
  /** Solo slot `hero`: la sidebar è nascosta sotto i 640px. */
  mobileImage: BannerImage | null;
  position: number;
  active: boolean;
  /** Informativo: nessuno scheduler lo filtra, serve al badge "scaduto". */
  endsAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BannerListResponse {
  items: Banner[];
  total: number;
}

export interface UploadedImage extends BannerImage {
  size: number;
  width: number;
  height: number;
}

export interface UploadImageResponse {
  banner: Banner;
  variant: BannerVariant;
  image: UploadedImage;
}

export interface BannerCreatePayload {
  slot: BannerSlot;
  alt: string;
  audience?: BannerAudience;
  href?: string | null;
  endsAt?: string | null;
  active?: boolean;
}

/**
 * Aggiornamento parziale: il backend scrive solo i campi presenti, quindi
 * omettere una chiave la lascia invariata mentre passarla a `null` la
 * azzera. Non collassare le due cose in un unico `undefined`.
 */
export type BannerUpdatePayload = Partial<{
  slot: BannerSlot;
  alt: string;
  audience: BannerAudience;
  href: string | null;
  endsAt: string | null;
  active: boolean;
}>;

/** URL di un asset banner, per un `<img src>`. */
export function bannerImageUrl(image: BannerImage | null | undefined): string | null {
  return image ? image.url : null;
}

/**
 * Asset da produrre, per Slot e variante — la parte di `asset-spec.md` che
 * il codice può controllare. Vive qui e non nella pagina admin perché è
 * conoscenza di dominio, non di presentazione.
 */
export interface AssetTarget {
  label: string;
  ratio: number;
  ratioLabel: string;
  width: number;
  height: number;
  /** Sotto questa larghezza l'asset è visibilmente sfocato sul box reso. */
  minWidth: number;
  maxBytes: number;
}

export const ASSET_TARGETS: Record<string, AssetTarget> = {
  'hero:desktop': {
    label: 'Hero desktop',
    ratio: 16 / 9,
    ratioLabel: '16:9',
    width: 1920,
    height: 1080,
    // Il box massimo è 864 px CSS: su schermo 2× servono 1728px.
    minWidth: 1728,
    maxBytes: 300_000,
  },
  'hero:mobile': {
    label: 'Hero mobile',
    ratio: 1,
    ratioLabel: '1:1',
    width: 1080,
    height: 1080,
    // Box reso 356–380 px CSS: 1080 copre anche i display 2,7×.
    minWidth: 1024,
    maxBytes: 200_000,
  },
  'sidebar:desktop': {
    label: 'Sidebar',
    ratio: 4 / 5,
    ratioLabel: '4:5',
    width: 880,
    height: 1100,
    // asset-spec.md accetta anche 660 × 825.
    minWidth: 660,
    maxBytes: 150_000,
  },
};

export function assetTargetFor(slot: BannerSlot, variant: BannerVariant): AssetTarget | null {
  return ASSET_TARGETS[`${slot}:${variant}`] ?? null;
}

// Il rapporto non è mai esatto al pixel (1920×1080 dà 1,7778, un asset
// 2752×1536 dà 1,792): il 3% assorbe gli arrotondamenti senza lasciar
// passare un 1:1 al posto di un 4:5, che sono a distanza del 25%.
const RATIO_TOLERANCE = 0.03;

export interface AssetCheck {
  /** Messaggi da mostrare all'admin. Mai bloccanti: l'asset è già salvato. */
  warnings: string[];
  target: AssetTarget | null;
}

/**
 * Confronta un asset caricato con `asset-spec.md`.
 *
 * Non blocca: serve a far sapere all'admin che l'asset non è conforme
 * *prima* di pubblicarlo, non a impedirgli di farlo — un banner fuori
 * specifica pubblicato consapevolmente è meglio di uno slot vuoto.
 */
export function checkAsset(
  slot: BannerSlot,
  variant: BannerVariant,
  image: { width: number; height: number; size: number },
): AssetCheck {
  const target = assetTargetFor(slot, variant);
  if (!target) return { warnings: [], target: null };

  const warnings: string[] = [];
  const ratio = image.width / image.height;

  if (Math.abs(ratio - target.ratio) / target.ratio > RATIO_TOLERANCE) {
    warnings.push(
      `Rapporto ${ratio.toFixed(2)} invece di ${target.ratioLabel} ` +
      `(${target.ratio.toFixed(2)}): l'immagine verrà ritagliata sui lati lunghi. ` +
      `Formato atteso: ${target.width} × ${target.height} px.`,
    );
  }

  if (image.width < target.minWidth) {
    warnings.push(
      `Larghezza ${image.width} px: sotto i ${target.minWidth} px l'immagine ` +
      `risulta sfocata sugli schermi ad alta densità. Formato atteso: ` +
      `${target.width} × ${target.height} px.`,
    );
  }

  if (image.size > target.maxBytes) {
    warnings.push(
      `Peso ${Math.round(image.size / 1024)} KB oltre i ` +
      `${Math.round(target.maxBytes / 1024)} KB previsti per questo slot.`,
    );
  }

  return { warnings, target };
}

/** Massimo consigliato nello slot `hero`: un carosello più lungo non viene visto. */
export const HERO_MAX_BANNERS = 5;

/** True se `endsAt` è passata. Il backend non filtra: è solo informativo. */
export function isExpired(endsAt: string | null): boolean {
  if (!endsAt) return false;
  const today = new Date().toISOString().slice(0, 10);
  return endsAt < today;
}

export const bannerService = {
  // ── Pubblico ───────────────────────────────────────────────────────────
  /**
   * Banner pubblicabili per lo Slot. Il filtro `audience` è applicato dal
   * backend in base al token della richiesta: qui non si filtra nulla.
   */
  listBySlot: async (slot: BannerSlot): Promise<Banner[]> => {
    const { data } = await api.get<BannerListResponse>('/api/banners', { params: { slot } });
    return data.items;
  },

  // ── Admin ──────────────────────────────────────────────────────────────
  adminList: async (): Promise<Banner[]> => {
    const { data } = await api.get<BannerListResponse>('/api/admin/banners');
    return data.items;
  },

  create: async (payload: BannerCreatePayload): Promise<Banner> => {
    const { data } = await api.post<Banner>('/api/admin/banners', payload);
    return data;
  },

  update: async (id: string, payload: BannerUpdatePayload): Promise<Banner> => {
    const { data } = await api.put<Banner>(`/api/admin/banners/${id}`, payload);
    return data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/api/admin/banners/${id}`);
  },

  uploadImage: async (
    id: string,
    variant: BannerVariant,
    file: File,
  ): Promise<UploadImageResponse> => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post<UploadImageResponse>(
      `/api/admin/banners/${id}/image`,
      form,
      { params: { variant }, headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  removeImage: async (id: string, variant: BannerVariant): Promise<void> => {
    await api.delete(`/api/admin/banners/${id}/image`, { params: { variant } });
  },

  reorder: async (slot: BannerSlot, ids: string[]): Promise<void> => {
    await api.put('/api/admin/banners/reorder', { slot, ids });
  },
};
